import React, { useState, useEffect } from 'react';
import { Trash2, CheckCircle, Clock, Phone, Loader2, Eye, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

import { PHP_API } from '../config/api';

const statusColors = {
  pending: 'bg-amber-100 text-amber-800',
  resolved: 'bg-green-100 text-green-800',
  contacted: 'bg-blue-100 text-blue-800',
};

const statusIcons = {
  pending: Clock,
  resolved: CheckCircle,
  contacted: Phone,
};

export default function AdminInquiries() {
  const { getAuthHeaders } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(PHP_API.inquiries, getAuthHeaders());
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (inquiry, newStatus) => {
    try {
      await axios.put(`${PHP_API.inquiryStatus}?id=${inquiry.id}&status=${newStatus}`, {}, getAuthHeaders());
      toast.success('Status updated');
      fetchInquiries();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleView = (inquiry) => {
    setSelectedInquiry(inquiry);
    setIsViewOpen(true);
  };

  const handleDelete = (inquiry) => {
    setSelectedInquiry(inquiry);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${PHP_API.inquiry}?id=${selectedInquiry.id}`, getAuthHeaders());
      toast.success('Inquiry deleted');
      setIsDeleteOpen(false);
      fetchInquiries();
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      toast.error('Failed to delete inquiry');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div data-testid="admin-inquiries">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl text-stone-900">Dealer Inquiries</h1>
        <p className="text-stone-600 mt-1">Manage dealer and distributor inquiries</p>
      </div>

      {/* Inquiries Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-stone-600">No inquiries yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Contact</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Company</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Location</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Date</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-stone-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {inquiries.map((inquiry) => {
                  const StatusIcon = statusIcons[inquiry.status];
                  return (
                    <tr key={inquiry.id} className="table-row-hover">
                      <td className="px-6 py-4">
                        <p className="font-medium text-stone-900">{inquiry.name}</p>
                        <p className="text-stone-500 text-sm">{inquiry.email}</p>
                      </td>
                      <td className="px-6 py-4 text-stone-600">{inquiry.company_name}</td>
                      <td className="px-6 py-4 text-stone-600">{inquiry.location}</td>
                      <td className="px-6 py-4">
                        <Select
                          value={inquiry.status}
                          onValueChange={(value) => handleStatusChange(inquiry, value)}
                        >
                          <SelectTrigger className="w-[130px]">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusColors[inquiry.status]}`}>
                              <StatusIcon className="w-3 h-3" />
                              {inquiry.status}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4 text-stone-500 text-sm">{formatDate(inquiry.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(inquiry)}
                            className="hover:bg-primary/10 hover:text-primary"
                            data-testid={`view-inquiry-${inquiry.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(inquiry)}
                            className="hover:bg-red-50 hover:text-red-600"
                            data-testid={`delete-inquiry-${inquiry.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Modal */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Inquiry Details</DialogTitle>
          </DialogHeader>

          {selectedInquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-stone-500 text-sm">Name</p>
                  <p className="font-medium text-stone-900">{selectedInquiry.name}</p>
                </div>
                <div>
                  <p className="text-stone-500 text-sm">Company</p>
                  <p className="font-medium text-stone-900">{selectedInquiry.company_name}</p>
                </div>
                <div>
                  <p className="text-stone-500 text-sm">Email</p>
                  <p className="font-medium text-stone-900">{selectedInquiry.email}</p>
                </div>
                <div>
                  <p className="text-stone-500 text-sm">Phone</p>
                  <p className="font-medium text-stone-900">{selectedInquiry.phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-stone-500 text-sm">Location</p>
                  <p className="font-medium text-stone-900">{selectedInquiry.location}</p>
                </div>
              </div>
              
              <div>
                <p className="text-stone-500 text-sm mb-2">Message</p>
                <div className="bg-stone-50 rounded-lg p-4">
                  <p className="text-stone-700">{selectedInquiry.message}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${statusColors[selectedInquiry.status]}`}>
                  {selectedInquiry.status}
                </span>
                <span className="text-stone-500 text-sm">{formatDate(selectedInquiry.created_at)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Inquiry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this inquiry from {selectedInquiry?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


